type Branch = {
  x: number;
  y: number;
  angle: number;
  length: number;
  targetLen: number;
  width: number;
  gen: number;
  branched: boolean;
  speed: number;
  wobble: number;
};

type Floater = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotVel: number;
  scale: number;
  alpha: number;
};

export type LinkedInBgOptions = {
  reducedMotion?: boolean;
  /** 0–1, orbit completion point where the surface revealed from */
  originXPct?: number;
  originYPct?: number;
};

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function spawnBranch(
  x: number,
  y: number,
  angle: number,
  targetLen: number,
  width: number,
  gen: number,
  rand: () => number,
): Branch {
  return {
    x,
    y,
    angle,
    length: 0,
    targetLen,
    width,
    gen,
    branched: false,
    speed: 14 + rand() * 22,
    wobble: rand() * Math.PI * 2,
  };
}

function spawnChildren(parent: Branch, rand: () => number, maxGen: number): Branch[] {
  if (parent.gen >= maxGen) return [];
  const tipX = parent.x + Math.cos(parent.angle) * parent.length;
  const tipY = parent.y + Math.sin(parent.angle) * parent.length;
  const count = parent.gen < 2 ? 2 + Math.floor(rand() * 2) : 2;
  const out: Branch[] = [];
  for (let i = 0; i < count; i += 1) {
    const spread = 0.32 + rand() * 0.48;
    const sign = i % 2 === 0 ? 1 : -1;
    const angle = parent.angle + sign * spread * (0.8 + rand() * 0.35);
    const targetLen = parent.targetLen * (0.45 + rand() * 0.35);
    const width = Math.max(0.4, parent.width * (0.55 + rand() * 0.2));
    out.push(spawnBranch(tipX, tipY, angle, targetLen, width, parent.gen + 1, rand));
  }
  return out;
}

function buildBranches(
  w: number,
  h: number,
  originXPct: number,
  originYPct: number,
  rand: () => number,
  scale: number,
): Branch[] {
  const ox = originXPct * w;
  const oy = originYPct * h;
  const branches: Branch[] = [];
  const burst = 10 + Math.floor(rand() * 4);

  for (let i = 0; i < burst; i += 1) {
    const angle = (i / burst) * Math.PI * 2 + (rand() - 0.5) * 0.55;
    branches.push(
      spawnBranch(ox, oy, angle, (90 + rand() * 110) * scale, 2.4, 0, rand),
    );
  }

  const anchors = [
    { x: w * 0.08, y: h * 0.92, a: -1.15 },
    { x: w * 0.22, y: h * 0.78, a: -0.75 },
    { x: w * 0.85, y: h * 0.15, a: 2.4 },
    { x: w * 0.92, y: h * 0.55, a: 3.0 },
  ];
  for (const anchor of anchors) {
    branches.push(
      spawnBranch(
        anchor.x,
        anchor.y,
        anchor.a + (rand() - 0.5) * 0.6,
        (60 + rand() * 80) * scale,
        1.8,
        0,
        rand,
      ),
    );
  }

  return branches;
}

function buildFloaters(w: number, h: number, rand: () => number, count: number): Floater[] {
  const out: Floater[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push({
      x: rand() * w,
      y: rand() * h,
      vx: (rand() - 0.5) * 16,
      vy: (rand() - 0.5) * 16,
      rotation: rand() * Math.PI * 2,
      rotVel: (rand() - 0.5) * 0.35,
      scale: 0.35 + rand() ** 0.8 * 1.45,
      alpha: 0.06 + rand() * 0.14,
    });
  }
  return out;
}

function createInTexture(fontSize: number): HTMLCanvasElement | null {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const font = `${fontSize}px BebasRegular, Impact, sans-serif`;
  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) return null;
  probe.font = font;
  const textW = probe.measureText('IN').width;
  const cssW = Math.ceil(textW + 16);
  const cssH = Math.ceil(fontSize * 1.3 + 8);
  const canvas = document.createElement('canvas');
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(dpr, dpr);
  ctx.font = font;
  ctx.fillStyle = '#f4efe4';
  ctx.textBaseline = 'middle';
  ctx.fillText('IN', 8, cssH / 2);
  return canvas;
}

function drawBranch(
  ctx: CanvasRenderingContext2D,
  b: Branch,
  t: number,
  ink: string,
  alpha: number,
): void {
  if (b.length < 0.5) return;
  const tipX = b.x + Math.cos(b.angle) * b.length;
  const tipY = b.y + Math.sin(b.angle) * b.length;
  const nx = -Math.sin(b.angle);
  const ny = Math.cos(b.angle);
  const bend = Math.sin(t * 0.45 + b.wobble) * (3 + b.gen);
  const cpx = b.x + Math.cos(b.angle) * b.length * 0.42 + nx * bend;
  const cpy = b.y + Math.sin(b.angle) * b.length * 0.42 + ny * bend;

  ctx.globalAlpha = alpha;
  ctx.strokeStyle = ink;
  ctx.lineWidth = b.width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(b.x, b.y);
  ctx.quadraticCurveTo(cpx, cpy, tipX, tipY);
  ctx.stroke();

  if (b.length > b.targetLen * 0.55 && b.gen >= 1) {
    ctx.fillStyle = ink;
    ctx.globalAlpha = alpha * 1.35;
    ctx.beginPath();
    ctx.arc(tipX, tipY, Math.max(1.2, b.width * 0.85), 0, Math.PI * 2);
    ctx.fill();
  }
}

export function mountLinkedInSurfaceBg(
  surface: HTMLElement | null,
  options: LinkedInBgOptions = {},
): () => void {
  if (!surface) return () => undefined;

  let canvas = surface.querySelector<HTMLCanvasElement>('.linkedin-bg-canvas');
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.className = 'linkedin-bg-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    surface.prepend(canvas);
  }

  const reducedMotion = options.reducedMotion ?? false;
  const originXPct = options.originXPct ?? 0.72;
  const originYPct = options.originYPct ?? 0.38;
  const rand = mulberry32(0x4c494e4b);
  const maxGen = reducedMotion ? 2 : 4;

  let branches: Branch[] = [];
  let floaters: Floater[] = [];
  let inTexture: HTMLCanvasElement | null = null;
  let cssW = 1;
  let cssH = 1;
  let pointer = { x: -9999, y: -9999 };
  let running = true;
  let raf = 0;
  let lastTs = 0;
  let t = 0;

  const onPointerMove = (e: PointerEvent): void => {
    const rect = surface.getBoundingClientRect();
    pointer.x = e.clientX - rect.left;
    pointer.y = e.clientY - rect.top;
  };

  const resize = (): void => {
    const rect = surface.getBoundingClientRect();
    cssW = Math.max(1, rect.width);
    cssH = Math.max(1, rect.height);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas!.width = Math.ceil(cssW * dpr);
    canvas!.height = Math.ceil(cssH * dpr);
    canvas!.style.width = `${cssW}px`;
    canvas!.style.height = `${cssH}px`;

    const ctx = canvas!.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const scale = Math.min(1.25, Math.max(0.7, cssW / 900));
    branches = buildBranches(cssW, cssH, originXPct, originYPct, rand, scale);
    floaters = buildFloaters(cssW, cssH, rand, Math.floor(55 + cssW * 0.04));
    inTexture = createInTexture(Math.max(14, cssW * 0.028));

    if (reducedMotion) {
      const extra: Branch[] = [];
      for (const b of branches) {
        b.length = b.targetLen;
        b.branched = true;
        for (const c of spawnChildren(b, rand, maxGen)) {
          c.length = c.targetLen;
          c.branched = true;
          extra.push(c);
        }
      }
      branches.push(...extra);
    }
  };

  const tick = (now: number): void => {
    if (!running) return;
    raf = requestAnimationFrame(tick);
    const ctx = canvas!.getContext('2d');
    if (!ctx) return;

    const dt = lastTs > 0 ? Math.min(0.05, (now - lastTs) / 1000) : 0.016;
    lastTs = now;
    t += dt;
    const motion = reducedMotion ? 0 : 1;

    if (motion > 0) {
      for (const b of branches) {
        if (b.length < b.targetLen) {
          b.length = Math.min(b.targetLen, b.length + b.speed * dt);
        }
        if (b.length >= b.targetLen * 0.86 && !b.branched) {
          b.branched = true;
          branches.push(...spawnChildren(b, rand, maxGen));
        }
      }

      for (const f of floaters) {
        const dx = pointer.x - f.x;
        const dy = pointer.y - f.y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < 160) {
          const pull = (1 - dist / 160) ** 2 * 22 * dt;
          f.vx += (dx / dist) * pull;
          f.vy += (dy / dist) * pull;
        }
        f.x += f.vx * dt;
        f.y += f.vy * dt;
        f.rotation += f.rotVel * dt;
        f.vx *= 0.992;
        f.vy *= 0.992;
        if (f.x < -40) f.x = cssW + 40;
        else if (f.x > cssW + 40) f.x = -40;
        if (f.y < -40) f.y = cssH + 40;
        else if (f.y > cssH + 40) f.y = -40;
      }
    }

    ctx.clearRect(0, 0, cssW, cssH);

    const ink = '#f4efe4';
    for (const b of branches) {
      const a = 0.14 + (1 - b.gen / (maxGen + 1)) * 0.12;
      drawBranch(ctx, b, t, ink, a);
    }

    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = '#ff2d95';
    for (const b of branches) {
      if (b.gen >= 2 && b.length > 8) {
        drawBranch(ctx, b, t + 0.5, '#ff2d95', 0.22);
      }
    }

    if (inTexture) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const tw = inTexture.width / dpr;
      const th = inTexture.height / dpr;
      for (const f of floaters) {
        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rotation);
        ctx.scale(f.scale, f.scale);
        ctx.globalAlpha = f.alpha;
        ctx.drawImage(inTexture, 0, 0, inTexture.width, inTexture.height, -tw / 2, -th / 2, tw, th);
        ctx.restore();
      }
    }

    ctx.globalAlpha = 1;
  };

  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(surface);
  window.addEventListener('resize', resize);
  window.addEventListener('pointermove', onPointerMove, { passive: true });
  raf = requestAnimationFrame(tick);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    ro.disconnect();
    window.removeEventListener('resize', resize);
    window.removeEventListener('pointermove', onPointerMove);
    const ctx = canvas!.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, cssW, cssH);
  };
}
